import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { IoCloseSharp } from "react-icons/io5";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CreateModal from "../../../component/Model/CreateModel";
import {
  useGetRecordDetailsQuery,
  useUpdateRecordMutation,
} from "../../../store/api/school/PraentSlice";
import {
  capitalizeFirstLetter,
  capitalizeString,
  handleApiError,
} from "../../../utils/dateAndTimeConversion";
import "../../AddVehicle.scss";
import { AddressAutocomplete } from "../../auth/parent-registration/SignUp";
import "./parent.scss";

const initialState = {
  full_name: "",
  email: "",
  phone: "",
  street: "",
  country: "",
  state: "",
  city: "",
  zip_code: "",
  latitude: "",
  longitude: "",
};

// Skeleton Input Component
const SkeletonInput = ({ labelWidth = "100px", className = "" }) => (
  <div className={`skeleton-input ${className}`}>
    <div
      className="skeleton-label mb-2"
      style={{
        width: labelWidth,
        height: "16px",
        background: "#f0f0f0",
        borderRadius: "4px",
      }}
    ></div>
    <div
      className="skeleton-field"
      style={{
        width: "100%",
        height: "40px",
        background: "#f0f0f0",
        borderRadius: "6px",
      }}
    ></div>
  </div>
);

const EditParent = () => {
  const { t } = useTranslation();
  const { role, id } = useParams();
  const navigate = useNavigate();
  const [show2, setShow2] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState(initialState);

  // Update Parent
  const [updateRecord, { isLoading }] = useUpdateRecordMutation();

  // Get parent details query
  const {
    data: parentData,
    isSuccess: isSuccessParent,
    isLoading: isGetLoading,
  } = useGetRecordDetailsQuery(`${id}`);

  const handleSetState = () => {
    setFormData({
      full_name: parentData.data?.full_name || "",
      email: parentData.data?.email || "",
      phone: parentData.data?.phone || "",
      street: parentData.data?.street || "",
      country: parentData.data?.country || "",
      state: parentData.data?.state || "",
      city: parentData.data?.city || "",
      zip_code: parentData.data?.zip_code || "",
      latitude: parentData.data?.latitude || "",
      longitude: parentData.data?.longitude || "",
    });
  };

  useEffect(() => {
    if (isSuccessParent && parentData?.data) {
      handleSetState();
    }
  }, [parentData, isSuccessParent]);

  const handleClose2 = () => {
    setShow2(false);
    navigate(`/parents`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: value
        ? ""
        : `${capitalizeFirstLetter(name.replace("_", " "))} is required`,
    }));
  };

  // Parent Address Autocomplete
  const handleParentAddressSelect = (address) => {
    setFormData((prev) => ({
      ...prev,
      ...address,
    }));

    setErrors((prev) => ({
      ...prev,
      street: address.street ? "" : `Street is required`,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    // Validate parent fields
    const requiredParentFields = ["full_name", "phone", "street"];

    requiredParentFields.forEach((field) => {
      if (!formData[field] || formData[field].trim() === "") {
        newErrors[field] = `${capitalizeString(field)} is required`;
      }
    });

    // Validate phone format (basic validation)
    if (formData.phone && !/^[\d\s\-\+\(\)]{10,}$/.test(formData.phone)) {
      newErrors.phone = "Phone number is invalid";
    }
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    toast.dismiss();
    try {
      let response = await updateRecord({
        data: formData,
        endPoint: id,
      }).unwrap();

      if (response.statusCode === 200 || response.statusCode === 201) {
        setShow2(true);
      }
    } catch (error) {
      handleApiError(error, t);
    }
  };

  const handleReset = () => {
    handleSetState();
  };

  return (
    <>
      <div className="outerBox">
        <div className="dbSubHd mb-3">
          {t("edit")} {t("parent")} {t("details")}
        </div>

        {isGetLoading ? (
          // Show skeleton loading when data is loading
          <div className="form-skeleton">
            <div className="row">
              <div className="col-12 col-md-4 mb-4">
                <SkeletonInput labelWidth="100px" />
              </div>
              <div className="col-12 col-md-4 mb-4">
                <SkeletonInput labelWidth="120px" />
              </div>
              <div className="col-12 col-md-4 mb-4">
                <SkeletonInput labelWidth="60px" />
              </div>
              <div className="col-12 mb-4">
                <SkeletonInput labelWidth="70px" />
              </div>
            </div>
            <div className="pt-4 d-flex">
              <div
                className="skeleton-button me-3"
                style={{
                  width: "100px",
                  height: "40px",
                  background: "#f0f0f0",
                  borderRadius: "4px",
                }}
              ></div>
              <div
                className="skeleton-button"
                style={{
                  width: "100px",
                  height: "40px",
                  background: "#f0f0f0",
                  borderRadius: "4px",
                }}
              ></div>
            </div>
          </div>
        ) : (
          <>
            <ul className="form d-flex flex-wrap p-0 pt-2">
              <li className="col-12 col-md-4">
                <label className="filedHd">
                  {t("full")} {t("name")}
                </label>
                <input
                  type="text"
                  className="formInput"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                />
                {errors?.full_name && (
                  <span style={{ color: "red" }}>{errors?.full_name}</span>
                )}
              </li>
              <li className="col-12 col-md-4">
                <label className="filedHd">
                  {t("email")} {t("address")}
                </label>
                <input
                  type="text"
                  className="formInput"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  readOnly
                />
                {errors?.email && (
                  <span style={{ color: "red" }}>{errors?.email}</span>
                )}
              </li>
              <li className="col-12 col-md-4">
                <label className="filedHd">{t("phone")}</label>
                <input
                  type="text"
                  className="formInput"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
                {errors?.phone && (
                  <span style={{ color: "red" }}>{errors?.phone}</span>
                )}
              </li>

              {/* Address with Same as Parent Checkbox */}
              <li className="col-12 addressInput">
                <label className="filedHd">{t("address")}</label>

                <AddressAutocomplete
                  value={formData.street}
                  onChange={handleChange}
                  onSelect={handleParentAddressSelect}
                />

                {errors?.street && (
                  <span style={{ color: "red" }}>{errors?.street}</span>
                )}
              </li>
            </ul>

            <div className="pt-4">
              <Button className="smallBtn cancel me-3" onClick={handleReset}>
                {t("btns.reset")}
              </Button>
              <Button
                className="smallBtn auto"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                ) : (
                  t("btns.submit")
                )}
              </Button>
            </div>
          </>
        )}
      </div>

      <Modal show={show2}>
        <Button className="closePopup" onClick={handleClose2}>
          <IoCloseSharp />
        </Button>
        <CreateModal
          onClose={handleClose2}
          title="Parent Update Successfully!"
          text="Parent has been updated."
        />
      </Modal>
    </>
  );
};

export default EditParent;
